import { MtDriveFile, MtDriveFolder, MtDriveVersion } from "server/models"

import {
    NEW, IN_PROGRESS, SUCCEEDED, FAILED,
    LIST, BLOCKS,
} from "const"
import { OrderType, StateType, StorageType } from "client/utils"

export type DriveOnSelectFile = (file: MtDriveFile) => void
export type DriveOnSelectFiles = (files: readonly MtDriveFile[]) => void
export type DriveOnSelectVersion = (version: MtDriveVersion, file: MtDriveFile) => void

export interface DriveFileProps {
    fileId: number
}

export type DriveMenuPlacement = "left" | "right"

export interface DriveFolder extends MtDriveFolder {
    readonly url: string
    readonly path: readonly MtDriveFolder[]
}
export interface DriveFile extends MtDriveFile {
    readonly url: string
}

export type NewFileStatusType = typeof NEW | typeof IN_PROGRESS | typeof SUCCEEDED | typeof FAILED
export interface NewFile {
    tempIndex: string
    name: string
    revision: string
    file: File
    status: NewFileStatusType
}
export type NewFileValidation = { [key in keyof NewFile]?: string }

export interface NestedFolder {
    folder: MtDriveFolder
    folders: { [folderName: string]: NestedFolder }
    files: { [fileName: string]: MtDriveFile }
}

export type FolderValidation = { [key in keyof MtDriveFolder]?: string }
export type FileValidation = { [key in keyof MtDriveFile]?: string }
export type VersionValidation = { [key in keyof MtDriveVersion]?: string }

export type FolderList = { [folderId: number]: MtDriveFolder }
export type FileList = { [fileId: number]: MtDriveFile }
export type VersionList = { [versionId: number]: MtDriveVersion }

export type DriveListStyle = typeof LIST | typeof BLOCKS

export type DriveSort = "updatedAt" | "id" | "name"

export interface DriveFilters {
    owner?: number
    storage?: StorageType
    search?: string
}

export interface DriveVersionFilters {
    search?: string
}

export const DRIVE_ACTION_RENAME = "RENAME"
export const DRIVE_ACTION_MOVE = "MOVE"
export const DRIVE_ACTION_DELETE = "DELETE"
export const DRIVE_ACTION_RESTORE = "RESTORE"
export const DRIVE_ACTION_CLEAR_RECYCLE = "CLEAR_RECYCLE"

export type DriveActionType = typeof DRIVE_ACTION_RENAME |
        typeof DRIVE_ACTION_MOVE |
        typeof DRIVE_ACTION_DELETE |
        typeof DRIVE_ACTION_RESTORE |
        typeof DRIVE_ACTION_CLEAR_RECYCLE

export interface DriveState {
    drivePath: readonly MtDriveFolder[]
    baseRoute: string
    // >>> don't change URL with these values
    outsideMode: boolean
    onSelectFile: DriveOnSelectFile
    onSelectFiles: DriveOnSelectFiles
    onSelectVersion: DriveOnSelectVersion
    // <<<

    listStyle: DriveListStyle

    sortColumn: DriveSort
    sortOrder: OrderType

    filters: DriveFilters
    searchState: StateType

    menuShow: boolean
    menuTarget: any
    menuPlacement: DriveMenuPlacement
    action: DriveActionType

    uploadFileList: NewFile[]

    folder: {
        folderList: readonly DriveFolder[]
        emptyList: boolean
        active: DriveFolder
        selected: readonly DriveFolder[]

        reloadList: boolean
        listLoadState: StateType
    }

    file: {
        fileList: readonly DriveFile[]
        emptyList: boolean
        active: DriveFile
        selected: readonly DriveFile[]

        offset: number
        limit: number
        loadMore: boolean

        fileEdit: MtDriveFile

        reloadList: boolean
        reloadEdit: boolean
        listLoadState: StateType
    }

    version: {
        versionList: VersionList
        versionIds: readonly number[]
        versionEdit: MtDriveVersion
        selected: readonly number[]

        sortColumn: keyof MtDriveVersion
        sortOrder: OrderType
        filters: DriveVersionFilters

        reloadList: boolean
        reloadEdit: boolean
    }
}


export const DRIVE_PATH = "DRIVE_PATH"
export const DRIVE_BASE_ROUTE = "DRIVE_BASE_ROUTE"

export const DRIVE_OUTSIDE_MODE = "DRIVE_OUTSIDE_MODE"
export const DRIVE_ON_SELECT_FILE = "DRIVE_ON_SELECT_FILE"
export const DRIVE_ON_SELECT_FILES = "DRIVE_ON_SELECT_FILES"
export const DRIVE_ON_SELECT_VERSION = "DRIVE_ON_SELECT_VERSION"

export const DRIVE_LIST_STYLE = "DRIVE_LIST_STYLE"

export const DRIVE_SORT_COLUMN = "DRIVE_SORT_COLUMN"
export const DRIVE_SORT_ORDER = "DRIVE_SORT_ORDER"

export const DRIVE_FILTERS = "DRIVE_FILTERS"
export const DRIVE_SEARCH_STATE = "DRIVE_SEARCH_STATE"

export const DRIVE_POPUP_MENU_SHOW = "DRIVE_POPUP_MENU_SHOW"
export const DRIVE_POPUP_MENU = "DRIVE_POPUP_MENU"
export const DRIVE_ACTION = "DRIVE_ACTION"

export const DRIVE_UPLOAD_FILE_LIST = "DRIVE_UPLOAD_FILE_LIST"
export const DRIVE_UPLOAD_FILE_LIST_STATUS = "DRIVE_UPLOAD_FILE_LIST_STATUS"

// folders
export const DRIVE_FOLDER_LIST = "DRIVE_FOLDER_LIST"
export const DRIVE_FOLDER_LIST_EMPTY = "DRIVE_FOLDER_LIST_EMPTY"
export const DRIVE_FOLDER_ACTIVE = "DRIVE_FOLDER_ACTIVE"
export const DRIVE_FOLDER_SELECTED = "DRIVE_FOLDER_SELECTED"
export const DRIVE_FOLDER_RELOAD_LIST = "DRIVE_FOLDER_RELOAD_LIST"
export const DRIVE_FOLDER_LIST_LOAD_STATE = "DRIVE_FOLDER_LIST_LOAD_STATE"

// files
export const DRIVE_FILE_LIST = "DRIVE_FILE_LIST"
export const DRIVE_FILE_LIST_EMPTY = "DRIVE_FILE_LIST_EMPTY"
export const DRIVE_FILE_ACTIVE = "DRIVE_FILE_ACTIVE"
export const DRIVE_FILE_SELECTED = "DRIVE_FILE_SELECTED"

export const DRIVE_FILE_OFFSET = "DRIVE_FILE_OFFSET"
export const DRIVE_FILE_LOAD_MORE = "DRIVE_FILE_LOAD_MORE"

export const DRIVE_FILE_EDIT = "DRIVE_FILE_EDIT"

export const DRIVE_FILE_RELOAD_LIST = "DRIVE_FILE_RELOAD_LIST"
export const DRIVE_FILE_RELOAD_EDIT = "DRIVE_FILE_RELOAD_EDIT"

export const DRIVE_FILE_LIST_LOAD_STATE = "DRIVE_FILE_LIST_LOAD_STATE"

// versions
export const DRIVE_VERSION_LIST = "DRIVE_VERSION_LIST"
export const DRIVE_VERSION_IDS = "DRIVE_VERSION_IDS"
export const DRIVE_VERSION_EDIT = "DRIVE_VERSION_EDIT"
export const DRIVE_VERSION_SELECTED = "DRIVE_VERSION_SELECTED"

export const DRIVE_VERSION_SORT = "DRIVE_VERSION_SORT"
export const DRIVE_VERSION_FILTERS = "DRIVE_VERSION_FILTERS"

export const DRIVE_VERSION_RELOAD_LIST = "DRIVE_VERSION_RELOAD_LIST"
export const DRIVE_VERSION_RELOAD_EDIT = "DRIVE_VERSION_RELOAD_EDIT"
